import { useEffect, useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Receipt, ArrowUUpLeft, CreditCard, WarningCircle, FileArrowDown } from "@phosphor-icons/react";
import { downloadPdf } from "./RevolutReceiptSettings";

const money = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const purposeLabel = (tx) => ({ subscription: "New subscription", renewal: "Subscription renewal", upgrade: "Plan change (prorated)", subscription_invoice: "Subscription invoice", licence: "One-time licence" }[tx.purpose] || (tx.plan_id ? `${tx.plan_id.replace(/_/g, " ")} plan${tx.billing_cycle ? ` — ${tx.billing_cycle}` : ""}` : "Subscription payment"));
const statusTone = (st) => ({ paid: "bg-emerald-100 text-emerald-700", partially_refunded: "bg-amber-100 text-amber-700", refunded: "bg-slate-200 text-slate-700", initiated: "bg-amber-100 text-amber-700" }[st] || "bg-red-100 text-red-700");

function RefundDialog({ tx, tenantName, onClose, onDone }) {
  const remaining = tx?.refundable_remaining || 0;
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAmount(remaining.toFixed(2)); setReason(""); }, [tx, remaining]);
  if (!tx) return null;
  const amt = Number(amount);
  const full = amt >= remaining - 0.005;
  const endsSub = full && ["subscription", "renewal"].includes(tx.purpose);
  const submit = async () => {
    if (!(amt > 0) || amt > remaining + 0.005) { toast.error(`Enter an amount between £0.01 and ${money(remaining)}`); return; }
    setBusy(true);
    try {
      const { data } = await API.post(`/platform/revolut/transactions/${tx.id}/refund`, { amount: amt, reason });
      toast.success(`${money(data.refund.amount)} refunded${data.refund.ended_subscription ? " — subscription ended" : ""}${data.refund.receipt_number ? ` · refund receipt ${data.refund.receipt_number} sent` : ""}`);
      onDone(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Refund failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowUUpLeft size={16} /> Refund payment</DialogTitle>
          <DialogDescription>{purposeLabel(tx)} · {money(tx.amount)} paid {fmtDate(tx.paid_at || tx.created_at)} by {tenantName}. {tx.refunded_total ? `${money(tx.refunded_total)} already refunded.` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button type="button" variant={full ? "default" : "outline"} size="sm" className="text-xs" onClick={() => setAmount(remaining.toFixed(2))} data-testid="refund-full-btn">Full refund · {money(remaining)}</Button>
            <Button type="button" variant={!full ? "default" : "outline"} size="sm" className="text-xs" onClick={() => setAmount((remaining / 2).toFixed(2))} data-testid="refund-partial-btn">Partial</Button>
          </div>
          <div>
            <Label className="text-xs">Amount (£)</Label>
            <Input type="number" min={0.01} max={remaining} step={0.01} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="refund-amount-input" />
          </div>
          <div>
            <Label className="text-xs">Reason (printed on the refund receipt)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Cancelled within 14 days" className="mt-1" data-testid="refund-reason-input" />
          </div>
          {endsSub && (
            <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid="refund-ends-warning"><WarningCircle size={14} className="mt-0.5 shrink-0" /> Refunding the full amount ends this workspace's subscription immediately — add-ons switch off and no further renewals are charged.</p>
          )}
          <p className="text-[11px] text-muted-foreground">The money goes back to the card used for this payment via Revolut (usually within 5–10 working days). The owner receives a refund receipt by e-mail.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="refund-cancel-btn">Cancel</Button>
            <Button size="sm" variant="destructive" onClick={submit} disabled={busy} data-testid="refund-confirm-btn">{busy ? "Refunding…" : `Refund ${money(amt || 0)}`}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TxRow({ tx, tenantName, onRefund }) {
  const dl = async (refundId, number) => {
    try { await downloadPdf(`/platform/revolut/transactions/${tx.id}/receipt.pdf${refundId ? `?refund_id=${refundId}` : ""}`, `${number || "receipt"}.pdf`); }
    catch { toast.error("Couldn't download the receipt"); }
  };
  const isRevolut = tx.gateway === "revolut";
  return (
    <div className="px-3 py-2.5 text-xs" data-testid={`billing-tx-${tx.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold truncate">{purposeLabel(tx)}{tx.seats ? ` · ${tx.seats} seats` : ""} <span className="ml-1 text-[9px] font-normal text-muted-foreground uppercase">{tx.gateway || "stripe"}</span></p>
          <p className="text-[10px] text-muted-foreground">{fmtDateTime(tx.paid_at || tx.created_at)}{tx.period_start ? ` · period ${fmtDate(tx.period_start)} – ${fmtDate(tx.period_end)}` : ""}{tx.receipt_number ? ` · ${tx.receipt_number}` : ""}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-bold font-[Chivo]">{money(tx.amount)}</p>
          <Badge className={`text-[8px] ${statusTone(tx.payment_status)}`} data-testid={`billing-tx-${tx.id}-status`}>{(tx.payment_status || "").replace("_", " ")}</Badge>
        </div>
      </div>
      {isRevolut && (tx.receipt_number || tx.refundable_remaining > 0) && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {tx.receipt_number && <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => dl(null, tx.receipt_number)} data-testid={`billing-tx-${tx.id}-receipt`}><FileArrowDown size={11} className="mr-1" /> Receipt {tx.receipt_number}</Button>}
          {tx.refundable_remaining > 0 && <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 text-destructive border-destructive/40 hover:bg-destructive/5" onClick={() => onRefund(tx)} data-testid={`billing-tx-${tx.id}-refund`}><ArrowUUpLeft size={11} className="mr-1" /> Refund{tx.refunded_total ? ` (up to ${money(tx.refundable_remaining)})` : ""}</Button>}
        </div>
      )}
      {(tx.refunds || []).length > 0 && (
        <ul className="mt-1.5 space-y-1 border-l-2 border-red-200 pl-2" data-testid={`billing-tx-${tx.id}-refunds`}>
          {tx.refunds.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-2 text-[10px] text-muted-foreground">
              <span className="font-semibold text-red-700">−{money(r.amount)}</span> refunded {fmtDateTime(r.refunded_at)}{r.refunded_by_name ? ` by ${r.refunded_by_name}` : ""}{r.reason ? ` · ${r.reason}` : ""}{r.ended_subscription ? " · subscription ended" : ""}
              {r.receipt_number && <button type="button" className="text-primary hover:underline" onClick={() => dl(r.id, r.receipt_number)} data-testid={`refund-${r.id}-receipt`}>{r.receipt_number}</button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function TenantBillingDrawer({ tenant, onClose }) {
  const [rec, setRec] = useState(null);
  const [refundTx, setRefundTx] = useState(null);
  const load = () => tenant && API.get(`/platform/tenants/${tenant.id}/billing`).then(({ data }) => setRec(data)).catch(() => toast.error("Couldn't load billing"));
  useEffect(() => { setRec(null); load(); }, [tenant?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!tenant) return null;
  const sub = rec?.subscription || {};
  const pm = sub.payment_method || {};
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="tenant-billing-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-[Chivo]"><Receipt size={18} /> {tenant.name} — billing</SheetTitle>
          <SheetDescription>Subscription, payments, receipts and refunds for this workspace.</SheetDescription>
        </SheetHeader>
        {!rec ? <p className="mt-6 text-xs text-muted-foreground">Loading…</p> : (
          <div className="mt-4 space-y-4">
            <div className="rounded-md border border-border p-3 text-xs space-y-1.5" data-testid="tenant-billing-summary">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`text-[9px] ${rec.tenant.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : rec.tenant.subscription_status === "past_due" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`} data-testid="tenant-billing-status">{rec.tenant.subscription_status || "—"}</Badge>
                <span className="font-semibold capitalize">{(rec.tenant.plan || "").replace(/_/g, " ")}</span>
                {rec.gateway && <span className="text-[10px] uppercase text-muted-foreground">via {rec.gateway}</span>}
                {rec.seats ? <span className="text-muted-foreground">· {rec.seats} seats</span> : null}
              </div>
              {rec.gateway === "revolut" && (
                <p className="flex items-center gap-1.5 text-muted-foreground"><CreditCard size={12} /> {pm.brand ? `${pm.brand.charAt(0).toUpperCase()}${pm.brand.slice(1)}` : "Card"}{pm.last4 ? ` •••• ${pm.last4}` : ""} · {sub.cycle || "monthly"} · {sub.status === "canceled" ? `ended ${fmtDate(sub.ended_at)}` : `renews ${fmtDate(sub.current_period_end)}`}{sub.cancel_at_period_end ? " · cancels at period end" : ""}</p>
              )}
              <p className="text-muted-foreground">Owner: {rec.owner?.name || "—"} {rec.owner?.email ? `<${rec.owner.email}>` : ""}</p>
              {rec.billing_alert && <p className="flex items-center gap-1 text-red-700" data-testid="tenant-billing-alert"><WarningCircle size={12} /> Payment failed {fmtDate(rec.billing_alert.failed_at)}{rec.billing_alert.decline_reason ? ` — ${rec.billing_alert.decline_reason}` : ""}</p>}
              <p className="text-muted-foreground">Paid in total {money(rec.totals?.paid)}{rec.totals?.refunded ? ` · refunded ${money(rec.totals.refunded)}` : ""}</p>
            </div>
            <div className="rounded-md border border-border overflow-hidden">
              <div className="px-3 py-2 bg-muted/40 border-b border-border text-xs font-semibold">Payments ({rec.transactions.length})</div>
              {rec.transactions.length === 0 ? <p className="px-3 py-6 text-xs text-muted-foreground text-center" data-testid="tenant-billing-empty">No payments yet</p> : (
                <div className="divide-y divide-border" data-testid="tenant-billing-transactions">{rec.transactions.map((tx) => <TxRow key={tx.id} tx={tx} tenantName={tenant.name} onRefund={setRefundTx} />)}</div>
              )}
            </div>
          </div>
        )}
        {refundTx && <RefundDialog tx={refundTx} tenantName={tenant.name} onClose={() => setRefundTx(null)} onDone={() => { setRefundTx(null); load(); }} />}
      </SheetContent>
    </Sheet>
  );
}
